import { useState } from "react";
import {
  Sparkles,
  Download,
  Trash2,
  Check,
  ChevronRight,
  Send,
  AlertCircle,
  Copy,
  Printer,
  FileSpreadsheet,
  Settings,
  MoreVertical,
  ShieldCheck,
  RefreshCw,
  Lock,
} from "lucide-react";
import {
  Button,
  IconButton,
  Badge,
  StatusBadge,
  WorkflowStatusBadge,
  Progress,
  Banner,
  WithTooltip,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  StatusStepper,
  type StatusStepperStep,
} from "@/shared/ui";
import { cn } from "@/shared/lib/cn";

export function TestUiCoreActionsFeedback() {
  const [btnLoading, setBtnLoading] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);
  const [progressVal, setProgressVal] = useState(68);
  const [activeStepIndex, setActiveStepIndex] = useState(5); // step 6 is "Đóng gói/Giao" (0-indexed: 5)
  const [stepperDensity, setStepperDensity] = useState<"default" | "compact">("compact");

  const commercialJourneySteps: StatusStepperStep[] = [
    {
      id: "draft",
      label: "Nháp",
      state: activeStepIndex > 0 ? "done" : activeStepIndex === 0 ? "current" : "upcoming",
      time: "28/09 08:00",
    },
    {
      id: "confirmed",
      label: "Xác nhận",
      state: activeStepIndex > 1 ? "done" : activeStepIndex === 1 ? "current" : "upcoming",
      time: "28/09 10:30",
    },
    {
      id: "demand",
      label: "Nhu cầu SX",
      state: "skipped",
      time: "Bỏ qua",
    },
    {
      id: "mo",
      label: "Lệnh SX",
      state: "skipped",
      time: "Bỏ qua",
    },
    {
      id: "ready",
      label: "Sẵn sàng TP",
      state: activeStepIndex > 4 ? "done" : activeStepIndex === 4 ? "current" : "upcoming",
      time: "28/09 14:15",
    },
    {
      id: "packing",
      label: "Đóng gói/Giao",
      state: activeStepIndex > 5 ? "done" : activeStepIndex === 5 ? "current" : "upcoming",
      time: activeStepIndex === 5 ? "Đang thực hiện" : activeStepIndex > 5 ? "28/09 15:45" : "Dự kiến 15:45",
    },
    {
      id: "completed",
      label: "Hoàn tất",
      state: activeStepIndex > 6 ? "done" : activeStepIndex === 6 ? "current" : "upcoming",
      time: activeStepIndex >= 6 ? "28/09 16:30" : "Dự kiến 16:30",
    },
  ];

  const handleSimulateAction = () => {
    setBtnLoading(true);
    setTimeout(() => {
      setBtnLoading(false);
      setProgressVal((prev) => (prev >= 100 ? 15 : prev + 25));
    }, 1200);
  };

  return (
    <div className="space-y-6">
      {/* 1. Button Variants & Sizes */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-md bg-primary-soft text-primary font-bold">
              <Sparkles className="size-4" />
            </div>
            <div>
              <h3 className="m-0 text-body font-bold text-text">
                1. Button & IconButton Primitives
              </h3>
              <p className="m-0 text-caption text-text-muted">
                Quy chuẩn hệ thống nút bấm với đầy đủ các biến thể tương tác, kích thước, icon và loading state
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<RefreshCw className={`size-3.5 ${btnLoading ? "animate-spin" : ""}`} />}
            onClick={handleSimulateAction}
            disabled={btnLoading}
          >
            {btnLoading ? "Đang xử lý..." : "Mô phỏng Loading"}
          </Button>
        </div>

        {/* Variants Matrix */}
        <div className="space-y-3">
          <span className="text-[11px] font-bold uppercase tracking-wider text-text-muted block">
            Biến thể màu sắc & phong cách (Variants)
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" leftIcon={<Sparkles className="size-4" />}>
              Primary Action
            </Button>
            <Button variant="secondary" leftIcon={<Download className="size-4" />}>
              Secondary Action
            </Button>
            <Button variant="soft" leftIcon={<Send className="size-4" />}>
              Soft Tone
            </Button>
            <Button variant="ghost" leftIcon={<Copy className="size-4" />}>
              Ghost Button
            </Button>
            <Button variant="danger" leftIcon={<Trash2 className="size-4" />}>
              Danger Action
            </Button>
          </div>
        </div>

        {/* Control Sizes */}
        <div className="space-y-3 pt-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-text-muted block">
            Kích thước tiêu chuẩn (Sizes: sm, md, lg)
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="primary">
              Kích thước Nhỏ (sm)
            </Button>
            <Button size="md" variant="primary">
              Kích thước Vừa (md)
            </Button>
            <Button size="lg" variant="primary">
              Kích thước Lớn (lg - Chuẩn form)
            </Button>
          </div>
        </div>

        {/* Interactive States */}
        <div className="space-y-3 pt-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-text-muted block">
            Các trạng thái tương tác đặc thù (Loading, Disabled, Right Icon)
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" loading={true}>
              Đang Lưu Dữ Liệu
            </Button>
            <Button variant="secondary" disabled={true} leftIcon={<Lock className="size-4" />}>
              Bị Vô Hiệu Hóa
            </Button>
            <Button variant="primary" rightIcon={<ChevronRight className="size-4" />}>
              Bước Tiếp Theo
            </Button>
          </div>
        </div>

        {/* IconButtons */}
        <div className="space-y-3 pt-2 border-t border-border/60">
          <span className="text-[11px] font-bold uppercase tracking-wider text-text-muted block">
            Nút Icon Độc Lập (IconButton - Kèm Tooltip tự động hỗ trợ A11y)
          </span>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <IconButton
                label="Sao chép mã lô"
                variant="ghost"
                icon={<Copy className="size-4" />}
              />
              <span className="text-caption text-text-muted">Ghost</span>
            </div>
            <div className="flex items-center gap-2">
              <IconButton
                label="In phiếu xuất kho"
                variant="secondary"
                icon={<Printer className="size-4" />}
              />
              <span className="text-caption text-text-muted">Secondary</span>
            </div>
            <div className="flex items-center gap-2">
              <IconButton
                label="Xóa bản ghi này"
                variant="danger"
                icon={<Trash2 className="size-4" />}
              />
              <span className="text-caption text-text-muted">Danger</span>
            </div>
            <div className="flex items-center gap-2">
              <IconButton
                label="Cấu hình hệ thống"
                variant="secondary"
                size="sm"
                icon={<Settings className="size-3.5" />}
              />
              <span className="text-caption text-text-muted">Size SM</span>
            </div>
            <div className="flex items-center gap-2">
              <IconButton
                label="Tùy chọn khác"
                variant="ghost"
                icon={<MoreVertical className="size-4" />}
              />
              <span className="text-caption text-text-muted">Menu Action</span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Badges & Workflow Statuses */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-4">
        <div className="border-b border-border/60 pb-2.5">
          <h3 className="m-0 text-body font-bold text-text">
            2. Badges, Status Badges & Workflow Indicators
          </h3>
          <p className="m-0 text-caption text-text-muted">
            Hệ thống huy hiệu trạng thái (Soft fills) trực quan, chuẩn hóa theo màu sắc nghiệp vụ ERP TM Food
          </p>
        </div>

        {/* Generic Semantic Badges */}
        <div className="space-y-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-text-muted block">
            Huy hiệu ngữ nghĩa cơ bản (Badge Tones)
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral" label="Neutral Tone" />
            <Badge tone="primary" label="Primary Brand" icon={<Sparkles />} />
            <Badge tone="success" label="Success / Hoàn thành" icon={<Check />} />
            <Badge tone="warning" label="Warning / Chờ xử lý" icon={<AlertCircle />} />
            <Badge tone="danger" label="Danger / Lỗi nghiêm trọng" icon={<Trash2 />} />
            <Badge tone="info" label="Info / Thông tin thêm" />
            <Badge tone="hold" label="Hold / Tạm giữ kiểm nghiệm" icon={<Lock className="size-3" />} />
            <Badge tone="accent" label="Accent / Đột xuất" />
          </div>
        </div>

        {/* StatusBadge Enterprise Patterns */}
        <div className="space-y-2 pt-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-text-muted block">
            StatusBadge ERP (Tự động map ngữ nghĩa trạng thái thực tế)
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status="active" />
            <StatusBadge status="confirmed" />
            <StatusBadge status="pending" />
            <StatusBadge status="quarantine" />
            <StatusBadge status="hold" />
            <StatusBadge status="failed" />
            <StatusBadge status="draft" />
            <StatusBadge status="closed" />
          </div>
        </div>

        {/* WorkflowStatusBadge */}
        <div className="space-y-2 pt-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-text-muted block">
            WorkflowStatusBadge (Trạng thái quy trình phê duyệt)
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <WorkflowStatusBadge status="draft" />
            <WorkflowStatusBadge status="pending" />
            <WorkflowStatusBadge status="approved" />
            <WorkflowStatusBadge status="rejected" />
          </div>
        </div>
      </div>

      {/* 3. Feedback: Banner & Progress */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
          <div>
            <h3 className="m-0 text-body font-bold text-text">
              3. Feedback: Banner Cảnh Báo & Tiến Độ (Progress)
            </h3>
            <p className="m-0 text-caption text-text-muted">
              Thông báo trên bề mặt màn hình và thanh tiến trình định lượng
            </p>
          </div>
          {!bannerVisible && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setBannerVisible(true)}
            >
              Hiện Lại Banner
            </Button>
          )}
        </div>

        {/* Banner Tones */}
        {bannerVisible && (
          <div className="space-y-3">
            <Banner
              tone="warning"
              title="Cảnh Báo Nhiệt Độ Kho Lạnh Vượt Ngưỡng Chuẩn"
              description="Kho lạnh CT-01 (Khu A) đang ghi nhận nhiệt độ -14.2°C (ngưỡng an toàn yêu cầu ≤ -18.0°C). Vui lòng kiểm tra dàn máy nén số 2."
              action={
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="primary">
                    Xem Log Sensor
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setBannerVisible(false)}>
                    Bỏ qua thông báo
                  </Button>
                </div>
              }
              onDismiss={() => setBannerVisible(false)}
            />

            <Banner
              tone="success"
              title="Đã Hoàn Tất Phân Bổ 12,500 kg Cá Tra Cho Lệnh MO-2026-0318"
              description="Hệ thống đã tự động khóa 2 lô nguyên liệu LOT-202609-08 và LOT-202609-12 tại Kho Sóc Trăng."
            />
          </div>
        )}

        {/* Progress & Journey Stepper */}
        <div className="space-y-4 pt-2">
          {/* 1. Linear Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-caption">
              <span className="font-semibold text-text">Tiến độ sản lượng kế hoạch:</span>
              <span className="font-mono font-bold text-primary">{progressVal}%</span>
            </div>
            <Progress value={progressVal} max={100} className="h-2.5" />
            <div className="flex items-center gap-2 pt-0.5">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setProgressVal((prev) => Math.max(0, prev - 10))}
              >
                -10%
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setProgressVal((prev) => Math.min(100, prev + 10))}
              >
                +10%
              </Button>
            </div>
          </div>

          {/* 2. Process Journey Stepper (StatusStepper Chuẩn ERP TM Food) */}
          <div className="space-y-3 pt-3 border-t border-border/60">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <span className="text-body font-bold text-text block">
                  Thanh Tiến Trình Quy Trình (StatusStepper Chuẩn ERP)
                </span>
                <span className="text-caption text-text-muted">
                  Thanh tiến trình mốc ngang chuẩn ERP kết nối các vòng tròn trạng thái kèm mốc thời gian bên dưới
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center rounded-md border border-border bg-bg p-0.5 text-caption">
                  <button
                    type="button"
                    onClick={() => setStepperDensity("compact")}
                    className={cn(
                      "px-2 py-0.5 rounded text-caption font-medium transition-colors cursor-pointer",
                      stepperDensity === "compact"
                        ? "bg-surface text-text shadow-xs font-semibold"
                        : "text-text-muted hover:text-text"
                    )}
                  >
                    Compact
                  </button>
                  <button
                    type="button"
                    onClick={() => setStepperDensity("default")}
                    className={cn(
                      "px-2 py-0.5 rounded text-caption font-medium transition-colors cursor-pointer",
                      stepperDensity === "default"
                        ? "bg-surface text-text shadow-xs font-semibold"
                        : "text-text-muted hover:text-text"
                    )}
                  >
                    Default
                  </button>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={activeStepIndex <= 0}
                  onClick={() => setActiveStepIndex((prev) => (prev === 4 ? 1 : Math.max(0, prev - 1)))}
                >
                  ◀ Bước Trước
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={activeStepIndex >= 6}
                  onClick={() => setActiveStepIndex((prev) => (prev === 1 ? 4 : Math.min(6, prev + 1)))}
                >
                  Bước Kế Tiếp ▶
                </Button>
              </div>
            </div>

            <div className="rounded-card border border-border bg-surface p-4 shadow-xs">
              <StatusStepper
                steps={commercialJourneySteps}
                density={stepperDensity}
                aria-label="Thanh tiến trình quy trình SO"
              />
            </div>
          </div>
        </div>

        {/* Tooltip Showcase */}
        <div className="space-y-2 pt-3 border-t border-border/60">
          <span className="text-[11px] font-bold uppercase tracking-wider text-text-muted block">
            Tooltip Primitives (Radix UI A11y Hover & Focus)
          </span>
          <div className="flex flex-wrap items-center gap-4">
            <WithTooltip label="Mã định danh duy nhất theo chuẩn ERP TM Food (bắt đầu bằng MO-)">
              <span className="cursor-help rounded border border-dashed border-border-strong px-2.5 py-1 text-caption font-mono text-text">
                Hover xem Tooltip: MO-2026-0318 ℹ️
              </span>
            </WithTooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="rounded-md bg-bg border border-border px-3 py-1 text-caption font-medium text-text hover:bg-surface cursor-pointer"
                >
                  Rê chuột vào đây
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                Nút kiểm tra quyền truy cập phân hệ MES
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
